import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaGetGmailPopSettings,
  betaUpdateGmailPopSettings,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { PopSettingsModel } from '../../../../../types';
import { Controller, useForm } from 'react-hook-form';

import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function PopSettingsPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();

  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const {
    data: gmailPopSettings,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.GmailPopSettings, userEmail],
    queryFn: () => betaGetGmailPopSettings(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('pop settings'),
    },
  });
  interface FormInput {
    accessWindow: string;
    disposition: string;
    disabled: boolean;
  }

  const {
    handleSubmit,
    watch,
    control,
    getValues,
    formState: { isDirty },
  } = useForm<FormInput>({
    values: {
      accessWindow:
        gmailPopSettings?.accessWindow === 'disabled'
          ? 'allMail'
          : gmailPopSettings?.accessWindow || 'allMail',
      disposition: gmailPopSettings?.disposition
        ? gmailPopSettings.disposition
        : 'archive',
      disabled: gmailPopSettings?.accessWindow === 'disabled',
    },
  });
  const onSubmit = (data: FormInput) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.EditGmailPopSettings.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (!data.disabled) {
        mutate({
          accessWindow: data.accessWindow,
          disposition: data.disposition,
        } as PopSettingsModel);
      } else {
        mutate({
          accessWindow: 'disabled',
          disposition: data.disposition,
        } as PopSettingsModel);
      }
    }
  };

  const { mutate, isPending: isUpdating } = useMutation({
    mutationFn: (data: PopSettingsModel) =>
      betaUpdateGmailPopSettings(userEmail, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('POP settings'),
      });
      queryClient.setQueryData([UserKeys.GmailPopSettings, userEmail], data);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('POP settings'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_pop_settings,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.popSettings,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const enablePopFor = [
    { value: 'allMail', displayValue: 'All Mail' },
    { value: 'fromNowOn', displayValue: 'Mail from now on' },
  ];

  const actionOptions = [
    { value: 'archive', displayValue: `Archive Gmail's copy` },
    { value: 'leaveInInbox', displayValue: `Keep Gmail's copy in the inbox` },
    { value: 'markRead', displayValue: `Mark Gmail's copy as read` },
    { value: 'trash', displayValue: `Delete Gmail's copy` },
  ];

  if (isLoading || isFetching) {
    return (
      <Card
        sx={{
          height: '100%',
        }}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', height: '100%' }}
        >
          <CircularProgress />
        </Stack>
      </Card>
    );
  }

  const disabledSwitch = watch('disabled');

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        action={
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={
              !isDirty ||
              !isAbleToSeeUIElement(
                userRoles,
                UserRoles.EditGmailPopSettings.value
              )
            }
            loading={isUpdating}
            color="primary"
            type="submit"
          >
            Save
          </Button>
        }
      />
      <CardContent
        sx={{
          px: 4,
          py: 0,
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Stack gap={3}>
          <Stack flexDirection="row" alignItems="center">
            <Typography>POP Enabled: </Typography>
            <Controller
              name={'disabled'}
              control={control}
              render={({ field }) => (
                <Switch
                  onChange={() => field.onChange(!field.value)}
                  checked={!field.value}
                />
              )}
            />
          </Stack>
          <Stack flexDirection={'row'} gap={2} alignItems="center">
            <Typography>Enable POP For:</Typography>
            <Controller
              name={'accessWindow'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  {...field}
                  disabled={disabledSwitch}
                  sx={{ width: 300 }}
                >
                  {enablePopFor.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Stack flexDirection={'row'} gap={11} alignItems="center">
            <Typography>Action:</Typography>
            <Controller
              name={'disposition'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  {...field}
                  disabled={disabledSwitch}
                  sx={{ width: 300 }}
                >
                  {actionOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              if (getValues('disabled') === false) {
                mutate({
                  accessWindow: getValues('accessWindow'),
                  disposition: getValues('disposition'),
                } as PopSettingsModel);
              } else {
                mutate({
                  accessWindow: 'disabled',
                  disposition: getValues('disposition'),
                } as PopSettingsModel);
              }
            }}
            mutateLoading={isUpdating}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
